import Phaser from 'phaser';

// ─── Pixel grid ───────────────────────────────────────────────────────────────
const PX   = 3;
const COLS = 48;
const ROWS = 72;
const CHAR_W = COLS * PX; // 144
const CHAR_H = ROWS * PX; // 216

// ─── Range helpers ────────────────────────────────────────────────────────────
const r   = (xS, xE, yS, yE) => { const o=[]; for(let y=yS;y<=yE;y++) for(let x=xS;x<=xE;x++) o.push([x,y]); return o; };
const row = (y, xS, xE) => r(xS, xE, y, y);
const col = (x, yS, yE) => r(x, x, yS, yE);

// ─── Skin tone palettes ───────────────────────────────────────────────────────
const SKIN_PALETTES = {
  base_default: { hi: 0xfde8c0, base: 0xf5c18a, mid: 0xe8a870, shadow: 0xd08848, dark: 0xb86830, neck: 0xeaaa60, neck_sh: 0xc88840 },
  base_tan:     { hi: 0xf0c890, base: 0xd4975a, mid: 0xc07840, shadow: 0xa85e28, dark: 0x904818, neck: 0xcc8848, neck_sh: 0xaa6830 },
  base_brown:   { hi: 0xc89060, base: 0x9a5c30, mid: 0x7a4020, shadow: 0x602c10, dark: 0x4a1e08, neck: 0x8a5228, neck_sh: 0x6e3e18 },
  base_dark:    { hi: 0x8c6040, base: 0x5a3018, mid: 0x3e1e0c, shadow: 0x2c1008, dark: 0x1e0804, neck: 0x4a2810, neck_sh: 0x3a1c08 },
};

// ─── Colour palette ───────────────────────────────────────────────────────────
const C = {
  // Hair
  hair_dark:    0x1e1408,
  hair_mid:     0x38260e,
  hair_hi:      0x5e3e1a,
  hair_shine:   0x7a5228,
  // Eyes
  eye_white:    0xf0f0ee,
  eye_iris:     0x2a4a9a,
  eye_pupil:    0x0a0a18,
  eye_shine:    0xffffff,
  eye_rim:      0x1a1a2a,
  // Face detail
  eyebrow:      0x251a08,
  nose:         0xcc7840,
  lip_top:      0xc84428,
  lip_bot:      0xb03820,
  lip_hi:       0xe06040,
  teeth:        0xeeeae4,
  blush:        0xee8866,
  // Tops
  top_white:    0xe8e8e8,
  top_grey:     0x808898,
  top_black:    0x1a1a22,
  top_gold:     0xcc8800,
  top_cosmic:   0x160e40,
  // Bottoms
  bot_blue:     0x1a3868,
  bot_dark:     0x0e0e14,
  bot_grey:     0x484858,
  bot_gold:     0x9a6e0e,
  bot_khaki:    0x847040,
  // Shoes
  shoe_dark:    0x0e0e0e,
  shoe_mid:     0x2a2a2a,
  shoe_sole:    0xd8d8c8,
  shoe_sole_hi: 0xeeeee0,
  // Accessories
  acc_red:      0xcc2222,
  acc_gold:     0xffd700,
  acc_gold_hi:  0xffe86a,
  acc_gold_sh:  0xaa8800,
  acc_halo:     0xfff060,
  // Handhelds
  joint_paper:  0xf0e8c8,
  joint_tip:    0x8a7250,
  ember:        0xff5500,
  // BG
  bg_plain:     0x0e1a28,
  bg_clouds:    0x4a90cc,
  bg_galaxy:    0x060612,
  bg_jungle:    0x101e0c,
};

// ─── Character pixel map (48×72 chibi grid) ────────────────────────────────────
const P = {
  // ── Hair ──────────────────────────────────────────────────────────────────
  hair_top:      r(10, 38,  0,  4),
  hair_main:     [...r( 8, 40,  5, 10), ...r( 6, 42, 11, 14)],
  hair_edge_l:   r( 6, 11,  5, 14),
  hair_edge_r:   r(36, 42,  5, 14),
  hair_hi_band:  r(13, 34,  0,  2),
  hair_shine:    r(16, 28,  3,  4),

  // ── Face ──────────────────────────────────────────────────────────────────
  face:          r( 8, 40,  6, 22),
  face_chin:     r(12, 36, 23, 25),
  face_hi:       r(17, 30,  7, 12),
  face_hi_chin:  r(18, 28, 22, 25),
  temple_l:      r( 8, 12, 10, 20),
  temple_r:      r(36, 40, 10, 20),

  // ── Ears ──────────────────────────────────────────────────────────────────
  ear_outer:     r(40, 45, 13, 21),
  ear_inner:     r(41, 44, 15, 19),
  ear_shadow:    r(40, 41, 13, 21),
  ear_l_outer:   r( 3,  8, 13, 21),
  ear_l_inner:   r( 4,  7, 15, 19),
  ear_l_shadow:  r( 7,  8, 13, 21),

  // ── Eyebrows ──────────────────────────────────────────────────────────────
  eyebrow_l:     [...r(10, 17,  8,  9), ...r(12, 15,  7,  7)],
  eyebrow_r:     [...r(30, 37,  8,  9), ...r(32, 35,  7,  7)],

  // ── Eyelashes ─────────────────────────────────────────────────────────────
  eyelash_top_l: row(10,  9, 17),
  eyelash_top_r: row(10, 29, 37),
  lower_lash_l:  row(16,  9, 17),
  lower_lash_r:  row(16, 29, 37),

  // ── Eyes (3×3 iris, chibi-expressive) ─────────────────────────────────────
  eyelid_l:      r( 9, 17, 10, 10),
  eyelid_r:      r(29, 37, 10, 10),
  eye_l_white:   r( 9, 18, 11, 16),
  eye_l_iris:    r( 9, 15, 11, 16),
  eye_l_pupil:   r(11, 13, 12, 14),
  eye_l_shine:   r( 9, 10, 11, 11),
  eye_r_white:   r(28, 37, 11, 16),
  eye_r_iris:    r(31, 37, 11, 16),
  eye_r_pupil:   r(33, 35, 12, 14),
  eye_r_shine:   r(36, 37, 11, 11),

  // ── Nose ──────────────────────────────────────────────────────────────────
  nose_bridge:   [...col(22, 17, 19), ...col(23, 17, 19)],
  nose_tip:      r(20, 26, 19, 20),
  nose_shadow:   [...col(20, 17, 19), ...col(25, 17, 19)],
  nostril_l:     r(17, 20, 20, 21),
  nostril_r:     r(25, 28, 20, 21),

  // ── Philtrum & chin ───────────────────────────────────────────────────────
  philtrum:      [...col(22, 20, 22), ...col(23, 20, 22)],
  chin_crease:   r(15, 31, 24, 25),

  // ── Cheek blush ───────────────────────────────────────────────────────────
  blush_l:       r( 7, 13, 17, 19),
  blush_r:       r(32, 38, 17, 19),

  // ── Mouth (closed, slight smile) ──────────────────────────────────────────
  lip_top:       r(16, 30, 22, 22),
  lip_bot:       r(16, 30, 23, 24),
  lip_hi:        r(18, 28, 22, 22),
  lip_bot_hi:    r(18, 26, 23, 23),
  mouth_corner:  [...r(14, 16, 22, 24), ...r(30, 32, 22, 24)],

  // ── Neck ──────────────────────────────────────────────────────────────────
  neck:          r(18, 28, 25, 29),
  neck_shadow_l: r(18, 20, 25, 29),
  neck_shadow_r: r(26, 28, 25, 29),
  neck_hi:       r(21, 24, 25, 27),

  // ── Torso / shoulders / arms (chibi — compact) ────────────────────────────
  shoulder_l:    r( 2,  9, 30, 35),
  shoulder_r:    r(38, 45, 30, 35),
  torso:         r( 9, 38, 30, 43),
  torso_hi:      r(17, 30, 31, 36),
  torso_shad_l:  r( 9, 12, 30, 43),
  torso_shad_r:  r(35, 38, 30, 43),
  arm_l:         r( 0,  9, 30, 46),
  arm_r:         r(38, 47, 30, 46),
  arm_l_hi:      r( 3,  5, 31, 44),
  arm_r_hi:      r(42, 44, 31, 44),
  arm_l_shad:    r( 0,  2, 30, 46),
  arm_r_shad:    r(45, 47, 30, 46),

  // ── Waist ─────────────────────────────────────────────────────────────────
  waist:         r( 9, 38, 44, 47),
  waist_shadow:  r( 9, 38, 44, 45),

  // ── Legs (chibi — short and chunky) ───────────────────────────────────────
  leg_l:         r( 9, 22, 48, 59),
  leg_r:         r(25, 38, 48, 59),
  leg_l_hi:      r(12, 16, 49, 58),
  leg_r_hi:      r(30, 34, 49, 58),
  leg_l_shad:    r( 9, 11, 48, 59),
  leg_r_shad:    r(36, 38, 48, 59),

  // ── Shoes (wide, rounded toe) ─────────────────────────────────────────────
  shoe_l:        [...r( 5, 24, 60, 65), ...r( 5, 26, 66, 67)],
  shoe_r:        [...r(23, 42, 60, 65), ...r(21, 42, 66, 67)],
  shoe_l_cap:    r( 5, 14, 60, 64),
  shoe_r_cap:    r(32, 42, 60, 64),
  shoe_sole_l:   r( 5, 26, 66, 67),
  shoe_sole_r:   r(21, 42, 66, 67),

  // ── Hands (big rounded chibi fists) ───────────────────────────────────────
  hand_l:        r( 0,  9, 46, 52),
  hand_r:        r(38, 47, 46, 52),
  hand_l_hi:     r( 2,  6, 47, 51),
  hand_r_hi:     r(40, 44, 47, 51),
  hand_l_knuckle: row(48,  1,  8),
  hand_r_knuckle: row(48, 39, 46),
  hand_l_thumb:  r( 9, 11, 46, 50),
  hand_r_thumb:  r(36, 38, 46, 50),

  // ── Knees ─────────────────────────────────────────────────────────────────
  knee_l_hi:     r(12, 17, 53, 55),
  knee_r_hi:     r(29, 34, 53, 55),
};

// ─── Background ───────────────────────────────────────────────────────────────
function drawBackground(gfx, bgKey, w, h) {
  gfx.clear();
  switch (bgKey) {
    case 'bg_clouds': {
      gfx.fillStyle(0x5588cc); gfx.fillRect(0,0,w,h);
      gfx.fillStyle(0x7ab0e0,0.5); gfx.fillRect(0,0,w,h*0.4);
      [[8,18,48,20,10],[62,28,55,16,8],[100,14,45,18,9],[20,110,60,18,9]].forEach(([x,y,ww,hh,rx]) => {
        gfx.fillStyle(0xffffff,0.85); gfx.fillRoundedRect(x,y,ww,hh,rx);
        gfx.fillStyle(0xe0eef8,0.5); gfx.fillRoundedRect(x,y+hh*0.5,ww,hh*0.5,{bl:rx,br:rx,tl:0,tr:0});
      });
      gfx.fillStyle(0xb8d4ea); gfx.fillRect(0,h*0.82,w,h*0.18);
      gfx.fillStyle(0x9abfd8); gfx.fillRect(0,h*0.88,w,h*0.12);
      break;
    }
    case 'bg_galaxy': {
      gfx.fillStyle(C.bg_galaxy); gfx.fillRect(0,0,w,h);
      [[10,6],[28,18],[82,10],[118,35],[18,55],[50,80],[94,65],[12,120],
       [72,140],[108,115],[42,28],[68,45],[98,85],[28,95],[58,16],[84,105],
       [38,70],[106,50],[20,40],[65,130],[90,30],[140,70],[130,20],[150,100]].forEach(([sx,sy]) => {
        gfx.fillStyle(0xffffff,0.8+Math.random()*0.2); gfx.fillRect(sx,sy,2,2);
      });
      [[35,50],[95,25],[155,85]].forEach(([sx,sy]) => {
        gfx.fillStyle(0xffffff,0.9);
        gfx.fillRect(sx-2,sy,6,2); gfx.fillRect(sx,sy-2,2,6);
      });
      gfx.fillStyle(0x4a0e8f,0.3); gfx.fillCircle(90,40,38);
      gfx.fillStyle(0x0e2a8f,0.2); gfx.fillCircle(30,110,30);
      gfx.fillStyle(0x8f0e4a,0.18); gfx.fillCircle(145,80,25);
      gfx.fillStyle(0x221144,0.5); gfx.fillRect(0,h*0.85,w,h*0.15);
      break;
    }
    case 'bg_jungle': {
      gfx.fillStyle(C.bg_jungle); gfx.fillRect(0,0,w,h);
      gfx.fillStyle(0x183a0a,0.9); gfx.fillRect(0,30,28,h); gfx.fillRect(w-26,20,26,h);
      gfx.fillStyle(0x0c2406,1);   gfx.fillRect(0,60,16,h); gfx.fillRect(w-16,50,16,h);
      gfx.fillStyle(0x2a6010,0.7); gfx.fillEllipse(14,45,40,30); gfx.fillEllipse(w-14,35,44,30);
      gfx.fillStyle(0x3a8020,0.5); gfx.fillEllipse(14,30,30,22); gfx.fillEllipse(w-12,24,34,22);
      gfx.fillStyle(0x2d5a1b); gfx.fillRect(0,h*0.82,w,h*0.18);
      gfx.fillStyle(0x1a3d0e); gfx.fillRect(0,h*0.86,w,h*0.14);
      break;
    }
    default: {
      gfx.fillStyle(C.bg_plain); gfx.fillRect(0,0,w,h);
      gfx.fillStyle(0x000000,0.3); gfx.fillRect(0,0,w,22); gfx.fillRect(0,h-22,w,22);
      gfx.fillStyle(0x000000,0.15); gfx.fillRect(0,0,18,h); gfx.fillRect(w-18,0,18,h);
      gfx.fillStyle(0x182840); gfx.fillRect(0,h*0.84,w,h*0.16);
      gfx.fillStyle(0x253d55,0.7); gfx.fillRect(0,h*0.84,w,3);
      gfx.fillStyle(0x38a169,0.06); gfx.fillTriangle(w/2-50,h*0.84, w/2+50,h*0.84, w/2,0);
    }
  }
}

// ─── Pixel fill helper ────────────────────────────────────────────────────────
function px(gfx, pixels, color, ox, oy, alpha = 1) {
  gfx.fillStyle(color, alpha);
  pixels.forEach(([x, y]) => gfx.fillRect(ox + x*PX, oy + y*PX, PX, PX));
}

// ─── Clothing shading helpers ─────────────────────────────────────────────────
function shadeTop(g, ox, oy) {
  px(g, P.torso_shad_l,  0x000000, ox, oy, 0.22);
  px(g, P.torso_shad_r,  0x000000, ox, oy, 0.22);
  px(g, P.arm_l_shad,    0x000000, ox, oy, 0.22);
  px(g, P.arm_r_shad,    0x000000, ox, oy, 0.22);
  px(g, P.torso_hi,      0xffffff, ox, oy, 0.10);
  px(g, P.arm_l_hi,      0xffffff, ox, oy, 0.10);
  px(g, P.arm_r_hi,      0xffffff, ox, oy, 0.10);
}

function shadeBottom(g, ox, oy) {
  px(g, P.leg_l_shad,    0x000000, ox, oy, 0.20);
  px(g, P.leg_r_shad,    0x000000, ox, oy, 0.20);
  px(g, P.leg_l_hi,      0xffffff, ox, oy, 0.10);
  px(g, P.leg_r_hi,      0xffffff, ox, oy, 0.10);
  px(g, P.waist_shadow,  0x000000, ox, oy, 0.18);
}

// ─── Accessory overlays ───────────────────────────────────────────────────────
const ACC = {
  acc_cap_red: {
    body:   r( 8, 40,  0,  9),
    brim:   r( 4, 44,  8, 11),
    peak:   r( 4, 24,  8, 13),
    band:   r( 8, 40,  8,  9),
    button: r(22, 25,  0,  1),
    holes:  [[14,4],[32,4],[14,7],[32,7]],
    hi:     r(10, 22,  0,  3),
    color:        0xcc2222,
    color_brim:   0xaa1818,
    color_band:   0x881010,
    color_peak:   0xaa1818,
    color_button: 0x881010,
    color_hi:     0xe03030,
    color_shade:  0x880e0e,
  },
  acc_beanie: {
    body:  r( 8, 40,  0, 11),
    band:  r( 6, 42, 10, 11),
    pom:   r(20, 27,  0,  3),
    pom_hi:r(21, 24,  0,  1),
    color:       0x2a6a2a,
    color_band:  0x1a4a1a,
    color_pom:   0x5cb85c,
    color_pom_hi:0x7ee07e,
  },
  acc_shades: {
    frame_l: r( 6, 19, 11, 18),
    frame_r: r(27, 40, 11, 18),
    lens_l:  r( 8, 17, 13, 17),
    lens_r:  r(29, 38, 13, 17),
    lens_hi_l: r( 8, 12, 13, 13),
    lens_hi_r: r(29, 33, 13, 13),
    bridge:  r(18, 27, 13, 14),
    arm_l:   r( 2,  7, 13, 14),
    arm_r:   r(39, 44, 13, 14),
    frame_hi_l: r( 6, 10, 11, 11),
    frame_hi_r: r(27, 31, 11, 11),
    color_frame:  0x1a1a1a,
    color_frame_hi: 0x444444,
    color_lens:   0x080820,
    color_lens_hi:0x1a3060,
    color_bridge: 0x222222,
  },
  acc_chain: {
    links:   [...r(14, 33, 36, 37), ...r(15, 16, 38, 44), ...r(31, 32, 38, 44), ...r(18, 29, 43, 46)],
    pendant: r(20, 27, 46, 50),
    pendant_hi: r(21, 25, 46, 47),
    link_hi: [...row(36, 16, 31), ...row(43, 19, 28)],
    color_link:    0xddb830,
    color_link_hi: 0xffd84a,
    color_pendant: 0xff7700,
    color_pendant_hi: 0xffaa44,
  },
  acc_crown: {
    base:    r( 6, 41,  4,  9),
    point_c: r(20, 27,  0,  3),
    point_l: r( 8, 13,  1,  4),
    point_r: r(34, 39,  1,  4),
    shade:   [...r( 6, 10, 4, 9), ...r(37, 41, 4, 9)],
    gems:    [r(14, 15, 4, 5), r(22, 23, 4, 5), r(30, 31, 4, 5)],
    gem_hi:  [r(14, 14, 4, 4), r(22, 22, 4, 4), r(30, 30, 4, 4)],
    hi:      [...r( 7, 10, 4, 6), ...r(20, 27, 0, 2)],
    color:       0xffd700,
    color_hi:    0xffe86a,
    color_shade: 0xaa8800,
    color_gem_r: 0xff2020,
    color_gem_b: 0x2244ff,
    color_gem_hi:0xffffff,
  },
  acc_halo: {
    glow3:  r( 4, 43,  0,  6),
    glow2:  r( 6, 41,  0,  4),
    glow1:  r( 8, 39,  0,  2),
    outer:  r( 6, 41,  0,  1),
    inner:  r(10, 37,  2,  3),
    shine:  [[12,0],[20,0],[28,0],[36,0]],
    color:       0xfff060,
    color_glow1: 0xffee00,
    color_glow2: 0xffcc00,
    color_glow3: 0xff9900,
    color_inner: 0xfffff0,
    color_shine: 0xffffff,
  },
  acc_bucket_hat: {
    top:     r(10, 37,  0,  6),
    crown_hi:r(12, 28,  0,  2),
    side_l:  r( 8,  9,  0,  9),
    side_r:  r(38, 39,  0,  9),
    brim:    r( 4, 43,  8, 11),
    brim_lo: r( 4, 43, 10, 11),
    color:      0xdddddd,
    color_hi:   0xeeeeee,
    color_brim: 0xcccccc,
    color_brim_lo: 0xaaaaaa,
  },
  acc_durag: {
    cap:    r( 6, 41,  0, 14),
    hi:     r( 8, 22,  0,  5),
    tail:   [...r(36, 45, 10, 15), ...r(38, 43, 16, 21)],
    knot:   r(34, 41, 10, 13),
    knot_hi:r(35, 38, 10, 11),
    fold_lines: [14, 22, 30],
    color:       0x111133,
    color_hi:    0x2222aa,
    color_knot:  0x222244,
    color_knot_hi:0x3333aa,
  },
  acc_headband: {
    band:   r( 6, 41,  8, 11),
    hi:     r( 8, 30,  8,  8),
    color:      0xcc2222,
    color_hi:   0xee4444,
  },
  acc_earring: {
    stud:  r(40, 41, 16, 17),
    drop:  r(40, 41, 18, 22),
    drop_hi: r(40, 40, 18, 20),
    color_stud:  0xffd700,
    color_drop:  0xffaa00,
    color_drop_hi:0xffee80,
  },
  // ── New accessories ────────────────────────────────────────────────────────
  acc_mask: {
    cover:    r( 8, 40, 16, 25),
    nose_gap: r(16, 30, 16, 18),   // visible nose bridge stays through
    detail_l: r( 8, 14, 17, 24),
    detail_r: r(34, 40, 17, 24),
    color:        0x111111,
    color_hi:     0x2a2a2a,
    color_mesh:   0x1c1c1c,
  },
  acc_bandana_neck: {
    main:   r( 9, 38, 26, 30),
    knot:   r(19, 28, 26, 28),
    tail_l: r( 9, 17, 27, 32),
    tail_r: r(30, 38, 27, 32),
    color:        0xcc2222,
    color_hi:     0xdd4444,
    color_shadow: 0x991818,
  },
  acc_mirror_shades: {
    frame_l:        r( 4, 19, 11, 19),
    frame_r:        r(27, 42, 11, 19),
    lens_l:         r( 6, 17, 13, 18),
    lens_r:         r(29, 40, 13, 18),
    bridge:         r(18, 27, 14, 15),
    arm_l:          r( 1,  5, 13, 14),
    arm_r:          r(41, 45, 13, 14),
    mirror_sweep_l: r( 6, 17, 13, 15),
    mirror_sweep_r: r(29, 40, 13, 15),
    frame_hi_l:     r( 4,  8, 11, 11),
    frame_hi_r:     r(27, 31, 11, 11),
    color_frame:    0xd4a017,
    color_frame_hi: 0xffd700,
    color_lens:     0xc8a000,
    color_lens_hi:  0xffe066,
    color_bridge:   0xb8900a,
  },
  acc_watch: {
    strap_top: r( 0,  8, 38, 40),
    face:      r( 1,  7, 36, 42),
    strap_bot: r( 0,  8, 42, 44),
    crown:     r( 7,  9, 38, 40),
    hand_h:    r( 3,  4, 37, 40),
    hand_m:    r( 2,  4, 37, 39),
    color_strap: 0xd4a017,
    color_face:  0xfff8e0,
    color_bezel: 0xb8900a,
    color_hands: 0x1a1a1a,
    color_crown: 0xd4a017,
  },
};

// ─── Handheld data ────────────────────────────────────────────────────────────
const HAND = {
  handheld_joint: {
    stick:   r(48, 53, 34, 52),
    tip:     r(48, 53, 34, 39),
    hi:      col(48, 36, 51),
    ember:   r(50, 51, 34, 35),
    color_stick: 0xf0e8c8,
    color_tip:   0x8a7250,
    color_hi:    0xfaf6e8,
  },
  handheld_spliff: {
    stick:   r(46, 53, 30, 52),
    tip:     r(46, 53, 30, 35),
    wrap1:   r(48, 49, 36, 52),
    hi:      col(46, 32, 51),
    ember:   r(48, 49, 30, 31),
    color_stick: 0xc8b040,
    color_tip:   0x9a8830,
    color_wrap:  0xa09030,
    color_hi:    0xdec858,
  },
};

// ─── Main Phaser Scene ────────────────────────────────────────────────────────
export default class AvatarScene extends Phaser.Scene {
  constructor() { super({ key: 'AvatarScene' }); }

  init(data) {
    this.avatarConfig = data.config || {};
    this.animated     = data.animated !== false;
    this._configKey   = null;
  }

  create() {
    const { width, height } = this.scale;
    this.bgGfx         = this.add.graphics();
    const cx = Math.floor((width  - CHAR_W) / 2);
    const cy = Math.floor((height - CHAR_H) / 2);
    this.charOrigin    = { x: cx, y: cy };
    this.charContainer = this.add.container(0, 0);
    this.bodyGfx    = this.add.graphics();
    this.topGfx     = this.add.graphics();
    this.bottomGfx  = this.add.graphics();
    this.accGfx     = this.add.graphics();
    this.handGfx    = this.add.graphics();
    this.fxGfx      = this.add.graphics();
    this.charContainer.add([this.bodyGfx, this.topGfx, this.bottomGfx, this.accGfx, this.handGfx, this.fxGfx]);
    this.particles    = null;
    this.emberTimer   = null;
    this.blinkTimer   = null;
    this.activeTweens = [];
    this.drawAll();
    if (this.animated) { this.startIdle(); this.scheduleBlink(); }
  }

  // ── Full redraw ─────────────────────────────────────────────────────────────
  drawAll() {
    if (!this.bodyGfx) return;
    const cfg = this.avatarConfig || {};
    const { x: ox, y: oy } = this.charOrigin;
    const { width, height } = this.scale;
    drawBackground(this.bgGfx, cfg.bg?.item_key || cfg.bg, width, height);
    this.bodyGfx.clear();   this.drawBody(ox, oy);
    this.topGfx.clear();    this.drawTop(cfg.top?.item_key, ox, oy);
    this.bottomGfx.clear(); this.drawBottom(cfg.bottom?.item_key, ox, oy);
    this.accGfx.clear();    this.drawAccessory(cfg.accessory?.item_key, ox, oy);
    this.handGfx.clear();   this.drawHandheld(cfg.handheld?.item_key, ox, oy);
    this.setupHandheldFx(cfg.handheld?.item_key, ox, oy);
  }

  // ── Body ────────────────────────────────────────────────────────────────────
  drawBody(ox, oy) {
    const g = this.bodyGfx;
    const cfg = this.avatarConfig || {};
    const baseKey = cfg.base_model?.item_key || cfg.base_model || 'base_default';
    const skin = SKIN_PALETTES[baseKey] || SKIN_PALETTES.base_default;

    // ── Hair ──
    px(g, P.hair_top,      C.hair_mid,   ox, oy);
    px(g, P.hair_main,     C.hair_dark,  ox, oy);
    px(g, P.hair_edge_l,   C.hair_dark,  ox, oy);
    px(g, P.hair_edge_r,   C.hair_dark,  ox, oy);
    px(g, P.hair_hi_band,  C.hair_hi,    ox, oy, 0.9);
    px(g, P.hair_shine,    C.hair_shine, ox, oy, 0.6);

    // ── Face base ──
    px(g, P.face,          skin.base,    ox, oy);
    px(g, P.face_chin,     skin.base,    ox, oy);

    // ── Face contouring ──
    px(g, P.face_hi,       skin.hi,      ox, oy, 0.55);
    px(g, P.face_hi_chin,  skin.hi,      ox, oy, 0.40);
    px(g, P.temple_l,      skin.shadow,  ox, oy, 0.12);
    px(g, P.temple_r,      skin.shadow,  ox, oy, 0.12);

    // ── Ears ──
    px(g, P.ear_outer,     skin.base,    ox, oy);
    px(g, P.ear_shadow,    skin.shadow,  ox, oy, 0.55);
    px(g, P.ear_inner,     skin.dark,    ox, oy, 0.45);
    px(g, P.ear_l_outer,   skin.base,    ox, oy);
    px(g, P.ear_l_shadow,  skin.shadow,  ox, oy, 0.55);
    px(g, P.ear_l_inner,   skin.dark,    ox, oy, 0.45);

    // ── Eyebrows ──
    px(g, P.eyebrow_l,     C.eyebrow,    ox, oy);
    px(g, P.eyebrow_r,     C.eyebrow,    ox, oy);

    // ── Eyelid rims ──
    px(g, P.eyelid_l,      C.eye_rim,    ox, oy, 0.5);
    px(g, P.eyelid_r,      C.eye_rim,    ox, oy, 0.5);

    // ── Eyelashes ──
    px(g, P.eyelash_top_l, C.eye_rim,    ox, oy, 0.85);
    px(g, P.eyelash_top_r, C.eye_rim,    ox, oy, 0.85);
    px(g, P.lower_lash_l,  C.eye_rim,    ox, oy, 0.28);
    px(g, P.lower_lash_r,  C.eye_rim,    ox, oy, 0.28);

    // ── Eyes ──
    this.drawEyes(true, ox, oy);

    // ── Cheek blush ──
    px(g, P.blush_l,       C.blush,      ox, oy, 0.28);
    px(g, P.blush_r,       C.blush,      ox, oy, 0.28);

    // ── Nose ──
    px(g, P.nose_bridge,   skin.hi,      ox, oy, 0.22);
    px(g, P.nose_shadow,   skin.shadow,  ox, oy, 0.18);
    px(g, P.nose_tip,      skin.mid,     ox, oy, 0.8);
    px(g, P.nostril_l,     skin.dark,    ox, oy, 0.38);
    px(g, P.nostril_r,     skin.dark,    ox, oy, 0.38);

    // ── Philtrum & chin ──
    px(g, P.philtrum,      skin.shadow,  ox, oy, 0.18);
    px(g, P.chin_crease,   skin.shadow,  ox, oy, 0.22);

    // ── Mouth ──
    px(g, P.lip_top,       C.lip_top,    ox, oy);
    px(g, P.lip_bot,       C.lip_bot,    ox, oy);
    px(g, P.lip_hi,        C.lip_hi,     ox, oy, 0.45);
    px(g, P.lip_bot_hi,    C.lip_hi,     ox, oy, 0.35);
    px(g, P.mouth_corner,  skin.dark,    ox, oy, 0.30);

    // ── Neck ──
    px(g, P.neck,          skin.neck,    ox, oy);
    px(g, P.neck_shadow_l, skin.neck_sh, ox, oy, 0.55);
    px(g, P.neck_shadow_r, skin.neck_sh, ox, oy, 0.55);
    px(g, P.neck_hi,       skin.hi,      ox, oy, 0.25);

    // ── Arms (base skin — overridden by top) ──
    px(g, P.arm_l,         skin.base,    ox, oy);
    px(g, P.arm_r,         skin.base,    ox, oy);

    // ── Hands ──
    px(g, P.hand_l,         skin.base,   ox, oy);
    px(g, P.hand_r,         skin.base,   ox, oy);
    px(g, P.hand_l_hi,      skin.hi,     ox, oy, 0.22);
    px(g, P.hand_r_hi,      skin.hi,     ox, oy, 0.22);
    px(g, P.hand_l_knuckle, skin.dark,   ox, oy, 0.32);
    px(g, P.hand_r_knuckle, skin.dark,   ox, oy, 0.32);
    px(g, P.hand_l_thumb,   skin.mid,    ox, oy, 0.70);
    px(g, P.hand_r_thumb,   skin.mid,    ox, oy, 0.70);

    // ── Shoes ──
    px(g, P.shoe_l,        C.shoe_dark,  ox, oy);
    px(g, P.shoe_r,        C.shoe_dark,  ox, oy);
    px(g, P.shoe_l_cap,    C.shoe_mid,   ox, oy, 0.6);
    px(g, P.shoe_r_cap,    C.shoe_mid,   ox, oy, 0.6);
    px(g, P.shoe_sole_l,   C.shoe_sole,  ox, oy);
    px(g, P.shoe_sole_r,   C.shoe_sole,  ox, oy);
    px(g, r( 5, 20, 66, 66), C.shoe_sole_hi, ox, oy, 0.5);
    px(g, r(21, 36, 66, 66), C.shoe_sole_hi, ox, oy, 0.5);

    // ── Knees ──
    px(g, P.knee_l_hi,     0xffffff,     ox, oy, 0.10);
    px(g, P.knee_r_hi,     0xffffff,     ox, oy, 0.10);
  }

  drawEyes(open, ox, oy) {
    const g = this.bodyGfx;
    const cfg = this.avatarConfig || {};
    const baseKey = cfg.base_model?.item_key || cfg.base_model || 'base_default';
    const skin = SKIN_PALETTES[baseKey] || SKIN_PALETTES.base_default;
    if (open) {
      px(g, P.eye_l_white, C.eye_white, ox, oy);
      px(g, P.eye_l_iris,  C.eye_iris,  ox, oy);
      px(g, P.eye_l_pupil, C.eye_pupil, ox, oy);
      px(g, P.eye_l_shine, C.eye_shine, ox, oy);
      px(g, P.eye_r_white, C.eye_white, ox, oy);
      px(g, P.eye_r_iris,  C.eye_iris,  ox, oy);
      px(g, P.eye_r_pupil, C.eye_pupil, ox, oy);
      px(g, P.eye_r_shine, C.eye_shine, ox, oy);
    } else {
      px(g, P.eye_l_white, skin.mid,    ox, oy);
      px(g, P.eye_r_white, skin.mid,    ox, oy);
      px(g, P.eyelid_l,    skin.shadow, ox, oy, 0.6);
      px(g, P.eyelid_r,    skin.shadow, ox, oy, 0.6);
    }
  }

  // ── Tops ──────────────────────────────────────────────────────────────────
  drawTop(itemKey, ox, oy) {
    const g = this.topGfx;
    const colorMap = {
      top_basic_tee:   C.top_white,
      top_hoodie:      C.top_grey,
      top_jacket:      C.top_black,
      top_gold_shirt:  C.top_gold,
      top_cosmic:      C.top_cosmic,
      top_polo:        0x1a3a6e,
      top_vest:        0xe0e0e0,
      top_flannel:     0x8b2020,
      top_leather:     0x0e0e0e,
      top_hawaiian:    0xd86030,
      top_varsity:     0x1a1a6e,
      top_bomber:      0x3e5020,
      top_denim_shirt: 0x325880,
      top_striped_tee: 0xccd8ee,
      top_hoodie_red:  0xbb1e1e,
    };
    const color = colorMap[itemKey] || C.top_white;

    px(g, P.torso,      color, ox, oy);
    px(g, P.shoulder_l, color, ox, oy);
    px(g, P.shoulder_r, color, ox, oy);
    px(g, P.arm_l,      color, ox, oy);
    px(g, P.arm_r,      color, ox, oy);

    shadeTop(g, ox, oy);

    switch (itemKey) {
      case 'top_basic_tee': {
        px(g, r(16, 30, 30, 31), 0xcccccc, ox, oy);
        px(g, r(18, 28, 30, 30), 0xdddddd, ox, oy);
        px(g, r( 9, 38, 42, 43), 0xdddddd, ox, oy, 0.4);
        px(g, col( 8, 30, 42),   0xcccccc, ox, oy, 0.4);
        px(g, col(39, 30, 42),   0xcccccc, ox, oy, 0.4);
        break;
      }
      case 'top_hoodie':
      case 'top_hoodie_red': {
        const hc = itemKey === 'top_hoodie_red' ? 0x991818 : 0x5a6a7a;
        px(g, r(14, 33, 30, 32), hc, ox, oy);
        px(g, r(13, 34, 39, 42), hc, ox, oy);
        px(g, r(14, 33, 39, 39), 0x000000, ox, oy, 0.25);
        px(g, r( 0,  7, 43, 45), hc, ox, oy);
        px(g, r(40, 47, 43, 45), hc, ox, oy);
        g.fillStyle(0x333333, 0.8);
        g.fillRect(ox+20*PX, oy+32*PX, PX, PX);
        g.fillRect(ox+26*PX, oy+32*PX, PX, PX);
        break;
      }
      case 'top_jacket': {
        px(g, [...r(16, 21, 30, 43), ...r(25, 30, 30, 43)], 0x2a2a38, ox, oy);
        px(g, col(17, 30, 42), 0x404050, ox, oy, 0.6);
        px(g, col(29, 30, 42), 0x404050, ox, oy, 0.6);
        [[23,33],[23,37],[23,41]].forEach(([bx,by]) => {
          g.fillStyle(0x666688, 1); g.fillRect(ox+bx*PX, oy+by*PX, 2*PX, 2*PX);
          g.fillStyle(0x9999bb, 0.6); g.fillRect(ox+bx*PX, oy+by*PX, PX, PX);
        });
        px(g, r(10, 15, 32, 36), 0x2a2a38, ox, oy);
        px(g, r(10, 15, 32, 32), 0x3a3a48, ox, oy);
        break;
      }
      case 'top_leather': {
        px(g, [...r(15, 21, 30, 43), ...r(25, 31, 30, 43)], 0x1a1a1a, ox, oy);
        px(g, col(15, 30, 43), 0x333333, ox, oy);
        px(g, col(31, 30, 43), 0x333333, ox, oy);
        px(g, col(23, 30, 43), 0x555555, ox, oy);
        px(g, col(24, 30, 43), 0x777777, ox, oy, 0.6);
        for (let yy=31; yy<=42; yy+=3) {
          g.fillStyle(0x888888,0.7); g.fillRect(ox+23*PX, oy+yy*PX, 2*PX, 2*PX);
        }
        px(g, col(4,  32, 44), 0x444444, ox, oy, 0.5);
        px(g, col(43, 32, 44), 0x444444, ox, oy, 0.5);
        break;
      }
      case 'top_flannel': {
        const flannelCheck = P.torso.filter(([,y]) => Math.floor(y/2) % 2 === 0);
        px(g, flannelCheck, 0x5a1010, ox, oy, 0.55);
        [10,15,20,27,34].forEach(x => px(g, col(x,30,43), 0x5a1010, ox, oy, 0.45));
        px(g, r(18, 28, 30, 32), 0x6a1818, ox, oy);
        px(g, r(20, 26, 30, 30), 0x7a2020, ox, oy);
        px(g, r(10, 17, 33, 38), 0x6a1818, ox, oy);
        px(g, r(10, 17, 33, 33), 0x7a2020, ox, oy);
        break;
      }
      case 'top_denim_shirt': {
        px(g, r(21, 26, 30, 43), 0x264870, ox, oy);
        px(g, col(23, 30, 43),   0x3a5a8a, ox, oy, 0.6);
        px(g, r(14, 32, 30, 32), 0x264870, ox, oy);
        px(g, r(16, 30, 30, 30), 0x3a5a8a, ox, oy);
        px(g, r(10, 18, 33, 38), 0x264870, ox, oy);
        px(g, r(10, 18, 33, 33), 0x3a5a8a, ox, oy);
        [32,36,40].forEach(yy => {
          g.fillStyle(0xaaccee,0.8); g.fillRect(ox+23*PX, oy+yy*PX, 2*PX, 2*PX);
        });
        px(g, r( 0,  7, 43, 45), 0x264870, ox, oy);
        px(g, r(40, 47, 43, 45), 0x264870, ox, oy);
        break;
      }
      case 'top_polo': {
        px(g, r(14, 32, 30, 32), 0x122848, ox, oy);
        px(g, r(16, 30, 30, 30), 0x1e3a5e, ox, oy);
        px(g, r(21, 26, 30, 37), 0x122848, ox, oy);
        [31,34,37].forEach(yy => {
          g.fillStyle(0xffffff,0.8); g.fillRect(ox+23*PX, oy+yy*PX, PX, PX);
        });
        px(g, r( 0,  7, 43, 45), 0x122848, ox, oy);
        px(g, r(40, 47, 43, 45), 0x122848, ox, oy);
        break;
      }
      case 'top_striped_tee': {
        const stripeRows = [...P.torso,...P.arm_l,...P.arm_r].filter(([,y]) => Math.floor(y/2)%2===0);
        px(g, stripeRows, 0x3a5fa0, ox, oy);
        px(g, r(16, 30, 30, 30), 0xb0c0d8, ox, oy);
        px(g, col( 8, 30, 42),   0xb0c0d8, ox, oy, 0.5);
        px(g, col(39, 30, 42),   0xb0c0d8, ox, oy, 0.5);
        break;
      }
      case 'top_varsity': {
        px(g, P.arm_l, 0xe8e8e8, ox, oy);
        px(g, P.arm_r, 0xe8e8e8, ox, oy);
        px(g, P.arm_l_shad, 0x000000, ox, oy, 0.15);
        px(g, P.arm_r_shad, 0x000000, ox, oy, 0.15);
        px(g, r(14, 32, 30, 32), 0xe8e8e8, ox, oy);
        px(g, r(16, 30, 30, 30), 0xffffff, ox, oy);
        [[18,33],[20,35],[22,37],[24,39],[26,37],[28,35],[30,33]].forEach(([vx,vy]) => {
          g.fillStyle(0xffd700,1); g.fillRect(ox+vx*PX, oy+vy*PX, 2*PX, 2*PX);
        });
        px(g, r( 0,  7, 40, 43), 0xffd700, ox, oy);
        px(g, r(40, 47, 40, 43), 0xffd700, ox, oy);
        px(g, r( 0,  7, 43, 45), 0xe8e8e8, ox, oy);
        px(g, r(40, 47, 43, 45), 0xe8e8e8, ox, oy);
        break;
      }
      case 'top_bomber': {
        px(g, r(12, 35, 30, 32), 0x2a3a15, ox, oy);
        px(g, r( 9, 38, 35, 36), 0x2a3a15, ox, oy, 0.5);
        px(g, r( 0,  7, 43, 45), 0x2a3a15, ox, oy);
        px(g, r(40, 47, 43, 45), 0x2a3a15, ox, oy);
        px(g, r( 9, 38, 40, 42), 0x2a3a15, ox, oy);
        px(g, col(23, 30, 42),   0x888866, ox, oy, 0.7);
        px(g, col(24, 30, 42),   0xaaaa88, ox, oy, 0.4);
        break;
      }
      case 'top_hawaiian': {
        px(g, r(18, 28, 30, 35), 0xb04828, ox, oy);
        const flowers = [[10,31,0xffdd00],[13,35,0xff88aa],[17,32,0xffdd00],
                          [32,34,0x88ff44],[35,31,0xff88aa],[38,33,0xffdd00],
                          [11,40,0x88ff44],[28,39,0xff88aa],[34,41,0xffdd00]];
        flowers.forEach(([fx,fy,fc]) => {
          g.fillStyle(fc,0.9); g.fillRect(ox+fx*PX, oy+fy*PX, 2*PX, 2*PX);
          g.fillStyle(fc,0.5); g.fillRect(ox+(fx-1)*PX, oy+fy*PX, PX, PX);
          g.fillStyle(fc,0.5); g.fillRect(ox+(fx+2)*PX, oy+fy*PX, PX, PX);
        });
        break;
      }
      case 'top_vest': {
        const cfg = this.avatarConfig || {};
        const baseKey = cfg.base_model?.item_key || cfg.base_model || 'base_default';
        const skin = SKIN_PALETTES[baseKey] || SKIN_PALETTES.base_default;
        px(g, P.arm_l, skin.base, ox, oy);
        px(g, P.arm_r, skin.base, ox, oy);
        px(g, P.arm_l_shad, skin.shadow, ox, oy, 0.25);
        px(g, P.arm_r_shad, skin.shadow, ox, oy, 0.25);
        px(g, r( 9, 12, 30, 43), 0xbbbbbb, ox, oy);
        px(g, r(36, 38, 30, 43), 0xbbbbbb, ox, oy);
        [31,35,39].forEach(yy => {
          g.fillStyle(0x999999,1); g.fillRect(ox+23*PX, oy+yy*PX, 2*PX, 2*PX);
          g.fillStyle(0xcccccc,0.6); g.fillRect(ox+23*PX, oy+yy*PX, PX, PX);
        });
        break;
      }
      case 'top_gold_shirt': {
        px(g, r(16, 30, 30, 31), 0xaa6e00, ox, oy);
        [31,35,39].forEach(yy => {
          g.fillStyle(0xffee80,1); g.fillRect(ox+23*PX, oy+yy*PX, PX, PX);
        });
        px(g, col(23,30,43), 0xffee80, ox, oy, 0.25);
        px(g, col(24,30,43), 0xffdd44, ox, oy, 0.20);
        break;
      }
      case 'top_cosmic': {
        px(g, r(14, 32, 30, 32), 0x0c0830, ox, oy);
        [[10,33],[18,36],[30,32],[38,38],[14,41],[36,41],[24,43]].forEach(([sx,sy]) => {
          g.fillStyle(0xffffff,0.7); g.fillRect(ox+sx*PX, oy+sy*PX, PX, PX);
          g.fillStyle(0xaaaaff,0.4); g.fillRect(ox+(sx-1)*PX, oy+sy*PX, PX, PX);
        });
        g.fillStyle(0x6030aa,0.15); g.fillCircle(ox+24*PX, oy+37*PX, 12);
        g.fillStyle(0x3060cc,0.10); g.fillCircle(ox+30*PX, oy+35*PX, 8);
        break;
      }
      default: break;
    }
  }

  // ── Bottoms ──────────────────────────────────────────────────────────────────
  drawBottom(itemKey, ox, oy) {
    const g = this.bottomGfx;
    const colorMap = {
      bottom_jeans:         C.bot_blue,
      bottom_shorts:        C.bot_grey,
      bottom_cargo:         C.bot_khaki,
      bottom_trackies:      C.bot_dark,
      bottom_gold:          C.bot_gold,
      bottom_chinos:        0xc0a060,
      bottom_joggers:       0x181818,
      bottom_denim_shorts:  0x264870,
      bottom_smart:         0x383848,
      bottom_camo:          0x485a28,
      bottom_linen:         0xe4dccc,
      bottom_leather_pants: 0x0e0e0e,
    };
    const color = colorMap[itemKey] || C.bot_blue;

    px(g, P.waist, color, ox, oy);
    px(g, P.leg_l, color, ox, oy);
    px(g, P.leg_r, color, ox, oy);

    shadeBottom(g, ox, oy);

    switch (itemKey) {
      case 'bottom_jeans': {
        px(g, col(14, 48, 59), 0x122050, ox, oy, 0.6);
        px(g, col(33, 48, 59), 0x122050, ox, oy, 0.6);
        px(g, r(10, 20, 52, 55), 0x2a4e8a, ox, oy, 0.3);
        px(g, r(27, 37, 52, 55), 0x2a4e8a, ox, oy, 0.3);
        px(g, r(27, 38, 48, 53), 0x122050, ox, oy, 0.4);
        px(g, r(27, 38, 48, 48), 0x1e3070, ox, oy, 0.6);
        [10,16,22,28,34].forEach(bx => {
          g.fillStyle(0x122050,0.7); g.fillRect(ox+bx*PX, oy+44*PX, 2*PX, 4*PX);
        });
        px(g, r( 9, 38, 44, 46), 0x1a1a1a, ox, oy, 0.7);
        g.fillStyle(0x888888,1); g.fillRect(ox+20*PX, oy+44*PX, 6*PX, 3*PX);
        g.fillStyle(0xaaaaaa,0.7); g.fillRect(ox+21*PX, oy+44*PX, 2*PX, PX);
        break;
      }
      case 'bottom_shorts': {
        px(g, r( 9, 22, 56, 58), 0x3a3a4a, ox, oy, 0.5);
        px(g, r(25, 38, 56, 58), 0x3a3a4a, ox, oy, 0.5);
        px(g, col( 9, 48, 58), 0x3a3a4a, ox, oy, 0.4);
        px(g, col(38, 48, 58), 0x3a3a4a, ox, oy, 0.4);
        px(g, r( 9, 14, 48, 53), 0x404050, ox, oy, 0.5);
        px(g, r(33, 38, 48, 53), 0x404050, ox, oy, 0.5);
        px(g, r( 9, 38, 44, 46), 0x3a3a4a, ox, oy, 0.6);
        break;
      }
      case 'bottom_cargo': {
        px(g, r( 9, 20, 51, 58), 0x6e5830, ox, oy);
        px(g, r(27, 38, 51, 58), 0x6e5830, ox, oy);
        px(g, r( 9, 20, 51, 53), 0x5e4820, ox, oy);
        px(g, r(27, 38, 51, 53), 0x5e4820, ox, oy);
        g.fillStyle(0x888866,0.8);
        g.fillRect(ox+13*PX, oy+52*PX, 2*PX, PX);
        g.fillRect(ox+31*PX, oy+52*PX, 2*PX, PX);
        px(g, col( 9, 48, 59), 0x5e4820, ox, oy, 0.4);
        px(g, col(38, 48, 59), 0x5e4820, ox, oy, 0.4);
        px(g, r( 9, 38, 44, 46), 0x5e4820, ox, oy);
        px(g, r(21, 26, 44, 47), 0x888866, ox, oy);
        break;
      }
      case 'bottom_trackies': {
        px(g, r( 9, 12, 44, 59), 0xffffff, ox, oy);
        px(g, r(35, 38, 44, 59), 0xffffff, ox, oy);
        px(g, r( 9, 38, 44, 47), 0x282828, ox, oy);
        px(g, r( 9, 38, 44, 45), 0x3a3a3a, ox, oy, 0.5);
        px(g, r( 9, 22, 57, 59), 0x282828, ox, oy);
        px(g, r(25, 38, 57, 59), 0x282828, ox, oy);
        break;
      }
      case 'bottom_joggers': {
        px(g, r( 9, 38, 44, 47), 0x222222, ox, oy);
        g.fillStyle(0x888888,0.7);
        g.fillRect(ox+18*PX, oy+45*PX, 4*PX, PX);
        g.fillRect(ox+24*PX, oy+45*PX, 4*PX, PX);
        px(g, r(10, 22, 57, 59), 0x222222, ox, oy);
        px(g, r(25, 37, 57, 59), 0x222222, ox, oy);
        [11,14,17,20].forEach(cx => px(g, col(cx,57,59), 0x333333, ox, oy, 0.5));
        [27,30,33,36].forEach(cx => px(g, col(cx,57,59), 0x333333, ox, oy, 0.5));
        break;
      }
      case 'bottom_smart': {
        px(g, col(14, 48, 59), 0x222230, ox, oy, 0.55);
        px(g, col(33, 48, 59), 0x222230, ox, oy, 0.55);
        px(g, r( 9, 38, 44, 47), 0x111111, ox, oy);
        g.fillStyle(0x888888,1); g.fillRect(ox+20*PX, oy+44*PX, 6*PX, 3*PX);
        g.fillStyle(0xaaaaaa,0.6); g.fillRect(ox+21*PX, oy+44*PX, 2*PX, PX);
        px(g, r(28, 38, 50, 50), 0x222230, ox, oy, 0.6);
        break;
      }
      case 'bottom_chinos': {
        px(g, r( 9, 38, 44, 46), 0x7a5828, ox, oy);
        px(g, col(14, 48, 59), 0xaa8848, ox, oy, 0.3);
        px(g, col(33, 48, 59), 0xaa8848, ox, oy, 0.3);
        px(g, r( 9, 16, 48, 53), 0xaa8848, ox, oy, 0.35);
        break;
      }
      case 'bottom_camo': {
        [
          [ 9, 17, 50, 55, 0x2a3a14],
          [28, 38, 48, 53, 0x2a3a14],
          [10, 19, 55, 58, 0x3a4e20],
          [27, 36, 55, 58, 0x3a4e20],
          [15, 21, 50, 53, 0x566030],
          [25, 33, 52, 57, 0x566030],
          [13, 20, 55, 58, 0x222a10],
          [30, 38, 53, 57, 0x222a10],
        ].forEach(([x1,x2,y1,y2,c]) => px(g, r(x1,x2,y1,y2), c, ox, oy));
        px(g, r( 9, 38, 44, 46), 0x2a3a14, ox, oy);
        break;
      }
      case 'bottom_denim_shorts': {
        [9,11,13,15,17,19,27,29,31,33,35,37].forEach(hx => {
          g.fillStyle(0x3a6a9a,0.6); g.fillRect(ox+hx*PX, oy+58*PX, PX, 2*PX);
        });
        px(g, r( 9, 22, 56, 58), 0x1e4070, ox, oy, 0.5);
        px(g, r(25, 38, 56, 58), 0x1e4070, ox, oy, 0.5);
        px(g, r( 9, 16, 48, 55), 0x1e4070, ox, oy, 0.5);
        px(g, r(33, 38, 48, 55), 0x1e4070, ox, oy, 0.5);
        px(g, r( 9, 38, 44, 46), 0x1e4070, ox, oy);
        break;
      }
      case 'bottom_linen': {
        [10,14,18,24,30,36].forEach(lx => {
          px(g, col(lx,48,59), 0xd4ccbc, ox, oy, 0.35);
        });
        [52,55,58].forEach(ly => px(g, r(9,38,ly,ly), 0xd4ccbc, ox, oy, 0.25));
        g.fillStyle(0xc4b8a0,0.7);
        g.fillRect(ox+18*PX, oy+45*PX, 4*PX, PX);
        g.fillRect(ox+24*PX, oy+45*PX, 4*PX, PX);
        break;
      }
      case 'bottom_leather_pants': {
        px(g, col(14, 48, 59), 0x222222, ox, oy);
        px(g, col(33, 48, 59), 0x222222, ox, oy);
        px(g, col(15, 48, 59), 0x2e2e2e, ox, oy, 0.6);
        px(g, col(34, 48, 59), 0x2e2e2e, ox, oy, 0.6);
        px(g, col( 9, 48, 59), 0x222222, ox, oy);
        px(g, col(38, 48, 59), 0x222222, ox, oy);
        px(g, r( 9, 38, 44, 47), 0x1a1a1a, ox, oy);
        break;
      }
      case 'bottom_gold': {
        px(g, col(15, 48, 59), 0xddaa00, ox, oy, 0.4);
        px(g, col(34, 48, 59), 0xddaa00, ox, oy, 0.4);
        px(g, r( 9, 38, 44, 46), 0x886600, ox, oy);
        g.fillStyle(0xffd700,1); g.fillRect(ox+20*PX, oy+44*PX, 6*PX, 3*PX);
        g.fillStyle(0xffe86a,0.6); g.fillRect(ox+21*PX, oy+44*PX, 2*PX, PX);
        break;
      }
      default: break;
    }
  }

  // ── Accessories ───────────────────────────────────────────────────────────────
  drawAccessory(itemKey, ox, oy) {
    if (!itemKey || !ACC[itemKey]) return;
    const g   = this.accGfx;
    const dat = ACC[itemKey];

    switch (itemKey) {
      case 'acc_cap_red': {
        px(g, dat.body,   dat.color,        ox, oy);
        px(g, dat.hi,     dat.color_hi,     ox, oy, 0.5);
        px(g, r(28,40,0,9), dat.color_shade, ox, oy, 0.3);
        px(g, dat.brim,   dat.color_brim,   ox, oy);
        px(g, r(4,24,10,11), 0x000000,      ox, oy, 0.35);
        px(g, dat.peak,   dat.color_peak,   ox, oy);
        px(g, dat.band,   dat.color_band,   ox, oy);
        px(g, dat.button, dat.color_button, ox, oy);
        dat.holes.forEach(([hx,hy]) => {
          g.fillStyle(0x881010,1); g.fillRect(ox+hx*PX, oy+hy*PX, PX, PX);
        });
        break;
      }
      case 'acc_beanie': {
        px(g, dat.body,   dat.color,       ox, oy);
        [14,18,22,26,30,34,38].forEach(x => {
          g.fillStyle(dat.color_band, 0.35);
          g.fillRect(ox+x*PX, oy, PX, 10*PX);
        });
        px(g, r(30,40,0,11), 0x000000, ox, oy, 0.2);
        px(g, dat.band,   dat.color_band,  ox, oy);
        g.fillStyle(dat.color_band, 0.4);
        g.fillRect(ox+6*PX, oy+10*PX, 36*PX, PX);
        px(g, dat.pom,    dat.color_pom,   ox, oy);
        px(g, dat.pom_hi, dat.color_pom_hi,ox, oy, 0.7);
        break;
      }
      case 'acc_shades': {
        px(g, dat.frame_l,    dat.color_frame,   ox, oy);
        px(g, dat.frame_r,    dat.color_frame,   ox, oy);
        px(g, dat.frame_hi_l, dat.color_frame_hi,ox, oy, 0.7);
        px(g, dat.frame_hi_r, dat.color_frame_hi,ox, oy, 0.7);
        px(g, dat.lens_l,     dat.color_lens,    ox, oy);
        px(g, dat.lens_r,     dat.color_lens,    ox, oy);
        px(g, dat.lens_hi_l,  dat.color_lens_hi, ox, oy, 0.5);
        px(g, dat.lens_hi_r,  dat.color_lens_hi, ox, oy, 0.5);
        g.fillStyle(0xffffff, 0.35);
        g.fillRect(ox+ 8*PX, oy+13*PX, PX, PX);
        g.fillRect(ox+29*PX, oy+13*PX, PX, PX);
        px(g, dat.bridge,  dat.color_bridge, ox, oy);
        px(g, dat.arm_l,   dat.color_frame,  ox, oy);
        px(g, dat.arm_r,   dat.color_frame,  ox, oy);
        break;
      }
      case 'acc_chain': {
        px(g, dat.links,      dat.color_link,    ox, oy);
        px(g, dat.link_hi,    dat.color_link_hi, ox, oy, 0.6);
        px(g, dat.pendant,    dat.color_pendant, ox, oy);
        px(g, dat.pendant_hi, dat.color_pendant_hi, ox, oy, 0.6);
        break;
      }
      case 'acc_crown': {
        px(g, dat.base,    dat.color,       ox, oy);
        px(g, dat.point_c, dat.color,       ox, oy);
        px(g, dat.point_l, dat.color,       ox, oy);
        px(g, dat.point_r, dat.color,       ox, oy);
        px(g, dat.hi,      dat.color_hi,    ox, oy, 0.65);
        px(g, dat.shade,   dat.color_shade, ox, oy, 0.5);
        px(g, dat.gems[0], dat.color_gem_r, ox, oy);
        px(g, dat.gems[1], dat.color,       ox, oy);
        px(g, dat.gems[2], dat.color_gem_b, ox, oy);
        px(g, dat.gem_hi[0], dat.color_gem_hi, ox, oy, 0.7);
        px(g, dat.gem_hi[1], dat.color_gem_hi, ox, oy, 0.7);
        px(g, dat.gem_hi[2], dat.color_gem_hi, ox, oy, 0.7);
        break;
      }
      case 'acc_halo': {
        px(g, dat.glow3, dat.color_glow3, ox, oy, 0.15);
        px(g, dat.glow2, dat.color_glow2, ox, oy, 0.25);
        px(g, dat.glow1, dat.color_glow1, ox, oy, 0.40);
        px(g, dat.outer, dat.color,       ox, oy);
        px(g, dat.inner, dat.color_inner, ox, oy, 0.5);
        dat.shine.forEach(([sx,sy]) => {
          g.fillStyle(dat.color_shine, 0.9);
          g.fillRect(ox+sx*PX, oy+sy*PX, PX, PX);
          g.fillStyle(dat.color_shine, 0.4);
          g.fillRect(ox+(sx-1)*PX, oy+sy*PX, PX, PX);
          g.fillRect(ox+(sx+1)*PX, oy+sy*PX, PX, PX);
        });
        break;
      }
      case 'acc_bucket_hat': {
        px(g, dat.top,      dat.color,       ox, oy);
        px(g, dat.crown_hi, dat.color_hi,    ox, oy, 0.6);
        px(g, r(30,39,0,9), 0x000000,        ox, oy, 0.15);
        px(g, dat.side_l,   dat.color,       ox, oy);
        px(g, dat.side_r,   dat.color,       ox, oy);
        px(g, dat.brim,     dat.color_brim,  ox, oy);
        px(g, dat.brim_lo,  dat.color_brim_lo, ox, oy);
        px(g, r(4,43,10,11), 0x000000,       ox, oy, 0.2);
        break;
      }
      case 'acc_durag': {
        px(g, dat.cap,  dat.color,     ox, oy);
        px(g, dat.hi,   dat.color_hi,  ox, oy, 0.4);
        dat.fold_lines.forEach(x => {
          g.fillStyle(dat.color_hi, 0.2);
          g.fillRect(ox+x*PX, oy, PX, 13*PX);
        });
        px(g, dat.tail, dat.color,     ox, oy);
        px(g, dat.knot, dat.color_knot,ox, oy);
        px(g, dat.knot_hi, dat.color_knot_hi, ox, oy, 0.6);
        break;
      }
      case 'acc_headband': {
        px(g, dat.band, dat.color,    ox, oy);
        px(g, dat.hi,   dat.color_hi, ox, oy, 0.5);
        px(g, r(6,41,11,11), 0x000000, ox, oy, 0.2);
        break;
      }
      case 'acc_earring': {
        px(g, dat.stud,    dat.color_stud,    ox, oy);
        px(g, dat.drop,    dat.color_drop,    ox, oy);
        px(g, dat.drop_hi, dat.color_drop_hi, ox, oy, 0.7);
        break;
      }
      // ── New accessories ──────────────────────────────────────────────────
      case 'acc_mask': {
        // Cover lower half of face
        px(g, dat.cover,    dat.color,      ox, oy);
        // Restore nose bridge area (let face show through slightly)
        px(g, dat.nose_gap, dat.color_mesh, ox, oy, 0.4);
        // Cheekbone highlights
        px(g, dat.detail_l, dat.color_hi,   ox, oy, 0.18);
        px(g, dat.detail_r, dat.color_hi,   ox, oy, 0.18);
        // Mesh texture: every other pixel lighter
        dat.cover.forEach(([mx,my],i) => {
          if ((mx + my) % 2 === 0) {
            g.fillStyle(dat.color_mesh, 0.3);
            g.fillRect(ox + mx*PX, oy + my*PX, PX, PX);
          }
        });
        break;
      }
      case 'acc_bandana_neck': {
        px(g, dat.tail_l, dat.color,        ox, oy);
        px(g, dat.tail_r, dat.color,        ox, oy);
        px(g, dat.main,   dat.color,        ox, oy);
        px(g, dat.knot,   dat.color_hi,     ox, oy);
        // Fold shadows on tails
        px(g, dat.tail_l, dat.color_shadow, ox, oy, 0.25);
        px(g, dat.tail_r, dat.color_shadow, ox, oy, 0.25);
        // Top edge highlight
        px(g, r( 9, 38, 26, 26), 0xffffff, ox, oy, 0.12);
        break;
      }
      case 'acc_mirror_shades': {
        px(g, dat.frame_l,        dat.color_frame,   ox, oy);
        px(g, dat.frame_r,        dat.color_frame,   ox, oy);
        px(g, dat.frame_hi_l,     dat.color_frame_hi,ox, oy, 0.7);
        px(g, dat.frame_hi_r,     dat.color_frame_hi,ox, oy, 0.7);
        px(g, dat.lens_l,         dat.color_lens,    ox, oy);
        px(g, dat.lens_r,         dat.color_lens,    ox, oy);
        // Mirror sweep — reflective top half tint
        px(g, dat.mirror_sweep_l, dat.color_lens_hi, ox, oy, 0.45);
        px(g, dat.mirror_sweep_r, dat.color_lens_hi, ox, oy, 0.45);
        // Reflection glint
        g.fillStyle(0xffffff, 0.5);
        g.fillRect(ox+ 7*PX, oy+13*PX, 2*PX, PX);
        g.fillRect(ox+30*PX, oy+13*PX, 2*PX, PX);
        px(g, dat.bridge,         dat.color_bridge,  ox, oy);
        px(g, dat.arm_l,          dat.color_frame,   ox, oy);
        px(g, dat.arm_r,          dat.color_frame,   ox, oy);
        break;
      }
      case 'acc_watch': {
        px(g, dat.strap_top, dat.color_strap,  ox, oy);
        px(g, dat.strap_bot, dat.color_strap,  ox, oy);
        // Bezel
        g.fillStyle(dat.color_bezel, 1);
        g.fillRect(ox+dat.face[0][0]*PX-PX, oy+dat.face[0][1]*PX-PX, 8*PX, 8*PX);
        // Dial
        px(g, dat.face,    dat.color_face,   ox, oy);
        // Crown
        px(g, dat.crown,   dat.color_crown,  ox, oy);
        // Hands
        px(g, dat.hand_h,  dat.color_hands,  ox, oy);
        px(g, dat.hand_m,  dat.color_hands,  ox, oy, 0.8);
        // Strap sheen
        px(g, r( 3,  5, 38, 44), 0xffd700, ox, oy, 0.3);
        break;
      }
      default: break;
    }
  }

  // ── Handheld ──────────────────────────────────────────────────────────────────
  drawHandheld(itemKey, ox, oy) {
    if (!itemKey || !HAND[itemKey]) return;
    const g   = this.handGfx;
    const dat = HAND[itemKey];
    px(g, dat.stick, dat.color_stick, ox, oy);
    px(g, dat.tip,   dat.color_tip,   ox, oy);
    if (dat.wrap1) px(g, dat.wrap1, dat.color_wrap, ox, oy);
    px(g, dat.hi,    dat.color_hi,    ox, oy, 0.55);
  }

  // ── Handheld FX ───────────────────────────────────────────────────────────────
  setupHandheldFx(itemKey, ox, oy) {
    if (this.emberTimer) { this.emberTimer.remove(); this.emberTimer = null; }
    if (this.particles)  { this.particles.destroy(); this.particles = null; }
    this.fxGfx.clear();
    if (!itemKey || !HAND[itemKey]) return;
    const dat = HAND[itemKey];
    const [ex, ey] = dat.ember[0];
    const ex_px = ox + ex*PX + PX/2;
    const ey_px = oy + ey*PX + PX/2;
    let on = true;
    this.emberTimer = this.time.addEvent({
      delay: 100, loop: true,
      callback: () => {
        this.fxGfx.clear();
        on = !on;
        if (on) {
          this.fxGfx.fillStyle(C.ember, 0.9);
          this.fxGfx.fillRect(ex_px-3, ey_px-3, 7, 7);
          this.fxGfx.fillStyle(0xff9944, 0.4);
          this.fxGfx.fillRect(ex_px-6, ey_px-6, 13, 13);
        }
      },
    });
    const smkGfx = this.make.graphics({ x:0, y:0, add:false });
    smkGfx.fillStyle(0xdddddd,1); smkGfx.fillCircle(4,4,4);
    smkGfx.generateTexture('smoke_p', 8, 8); smkGfx.destroy();
    this.particles = this.add.particles(ex_px, ey_px-4, 'smoke_p', {
      speed: { min:8, max:22 }, angle: { min:255, max:285 },
      scale: { start:0.4, end:0 }, alpha: { start:0.55, end:0 },
      lifespan: { min:700, max:1400 },
      frequency: itemKey === 'handheld_spliff' ? 70 : 130,
      quantity: 1, tint: [0xbbbbbb, 0x999999, 0xdddddd],
    });
  }

  // ── Idle ──────────────────────────────────────────────────────────────────────
  startIdle() { this.killTweens(); }

  // ── Blink ─────────────────────────────────────────────────────────────────────
  scheduleBlink() {
    if (this.blinkTimer) { this.blinkTimer.remove(); this.blinkTimer = null; }
    this.blinkTimer = this.time.delayedCall(Phaser.Math.Between(2000,5000), () => {
      const { x:ox, y:oy } = this.charOrigin;
      this.drawEyes(false, ox, oy);
      this.time.delayedCall(110, () => { this.drawEyes(true, ox, oy); this.scheduleBlink(); });
    });
  }

  killTweens() {
    this.activeTweens.forEach(t => t.remove());
    this.activeTweens = [];
    this.charContainer.setX(0); this.charContainer.setY(0);
  }

  // ── Emotes ────────────────────────────────────────────────────────────────────
  playEmote(key, onDone) {
    switch (key) {
      case 'emote_wave':  this.doWave(onDone);  break;
      case 'emote_jump':  this.doJump(onDone);  break;
      case 'emote_dance': this.doDance(onDone); break;
      case 'emote_smoke': this.doSmoke(onDone); break;
      case 'emote_peace': this.doWave(onDone);  break;
      default: if (onDone) onDone();
    }
  }

  doWave(cb) {
    this.killTweens();
    const t = this.tweens.add({
      targets: this.charContainer, x: 10,
      duration: 120, ease: 'Sine.easeInOut', yoyo: true, repeat: 4,
      onComplete: () => { this.charContainer.setX(0); this.startIdle(); if(cb) cb(); },
    });
    this.activeTweens.push(t);
  }

  doJump(cb) {
    this.killTweens();
    const t = this.tweens.add({
      targets: this.charContainer, y: -20,
      duration: 260, ease: 'Power2.easeOut', yoyo: true,
      onComplete: () => { this.charContainer.setY(0); this.startIdle(); if(cb) cb(); },
    });
    this.activeTweens.push(t);
  }

  doDance(cb) {
    this.killTweens();
    const steps = [[-14,-10],[14,0],[-14,-10],[14,0],[-14,-10],[14,0],[0,0]];
    let step = 0;
    const next = () => {
      if (step >= steps.length) {
        this.charContainer.setX(0); this.charContainer.setY(0);
        this.startIdle(); if(cb) cb(); return;
      }
      const [tx,ty] = steps[step++];
      const t = this.tweens.add({
        targets: this.charContainer, x:tx, y:ty,
        duration:130, ease:'Sine.easeInOut', onComplete: next,
      });
      this.activeTweens.push(t);
    };
    next();
  }

  doSmoke(cb) {
    const { x:ox, y:oy } = this.charOrigin;
    const mx = ox + 24*PX, my = oy + 22*PX;
    const sg = this.make.graphics({ x:0, y:0, add:false });
    sg.fillStyle(0xaaaaaa,1); sg.fillCircle(6,6,6);
    sg.generateTexture('big_s', 12, 12); sg.destroy();
    const burst = this.add.particles(mx, my, 'big_s', {
      speed:{min:25,max:55}, angle:{min:200,max:340},
      scale:{start:0.9,end:0}, alpha:{start:0.7,end:0},
      lifespan:1100, quantity:14, emitting:false,
    });
    burst.explode(14);
    this.time.delayedCall(1300, () => { burst.destroy(); if(cb) cb(); });
  }

  // ── Public API ────────────────────────────────────────────────────────────────
  updateConfig(config) {
    const key = JSON.stringify(config);
    if (key === this._configKey) return;
    this._configKey   = key;
    this.avatarConfig = config;
    if (this.bodyGfx) this.drawAll();
  }

  shutdown() {
    this.killTweens();
    if (this.blinkTimer) this.blinkTimer.remove();
    if (this.emberTimer) this.emberTimer.remove();
    if (this.particles)  this.particles.destroy();
  }
}
